import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { login, confirmLoginChoice, clearSsoSuppressed, mfaVerify, mfaEnrolStart, mfaEnrolConfirm } from '@/api/auth'
import { MfaCodeForm, MfaSetup } from '@twasta/page-runtime/shell'
import { getAppConfig } from '@/api/config'
import { useProjectStore } from '@/store/projectStore'
import toast from 'react-hot-toast'
import logoLarge from '@/assets/logo-large.png'

const FEATURES = [
  {
    icon: 'auto_awesome',
    title: 'AI-Powered App Generation',
    description: 'Describe your application in natural language and watch as AI generates a complete specification, database schema, and working code.',
  },
  {
    icon: 'design_services',
    title: 'Visual Designers',
    description: 'Design databases, workflows, and transaction objects with intuitive drag-and-drop visual tools. No coding required.',
  },
  {
    icon: 'code',
    title: 'Source Code Editor',
    description: 'Full-featured web IDE with syntax highlighting, AI code assistance, and real-time collaboration capabilities.',
  },
  {
    icon: 'integration_instructions',
    title: 'Git Integration',
    description: 'Built-in GitHub and GitLab integration. Push, pull, and manage repositories directly from the platform.',
  },
  {
    icon: 'psychology',
    title: 'Multi-LLM Support',
    description: 'Choose from OpenAI, Anthropic Claude, or Google Gemini. Bring your own API key or use our managed service.',
  },
]

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, refreshUser } = useAuth()

  // Beta instances have self-service sign-up closed: offer the waitlist only.
  // Default to closed so a config read failure never advertises a /register
  // page the backend would reject.
  const [signupEnabled, setSignupEnabled] = useState(false)
  useEffect(() => {
    getAppConfig()
      .then((cfg) => setSignupEnabled(!!cfg.signup_enabled))
      .catch(() => setSignupEnabled(false))
  }, [])

  // Optional post-login destination (e.g. set by the public website viewer
  // when a visitor hits a login-gated page, or by /api/auth/runtime-authorize
  // for SSO handoffs like IOFlow's "Authenticate with Twasta"). Only honor
  // internal paths to avoid open-redirect; ignore absolute/protocol-relative
  // URLs.
  // Landing from the emailed unlock link (/api/auth/unlock → ?unlocked=1|0).
  const unlockedParam = searchParams.get('unlocked')
  useEffect(() => {
    if (unlockedParam === '1') {
      toast.success('Your account is unlocked. Sign in to continue.')
    } else if (unlockedParam === '0') {
      toast.error('That unlock link has expired or was already used. Reset your password to unlock the account.')
    }
  }, [unlockedParam])

  const nextParam = searchParams.get('next')
  const nextPath = nextParam && /^\/(?!\/)/.test(nextParam) ? nextParam : null

  // Backend routes (e.g. /api/auth/runtime-authorize?...) need a FULL page
  // navigation — React Router has no such route, navigate() would blank out.
  const goNext = useCallback((fallback: string) => {
    if (nextPath && nextPath.startsWith('/api/')) {
      window.location.assign(nextPath)
      return
    }
    navigate(nextPath || fallback, { replace: true })
  }, [navigate, nextPath])

  // If this user had a project open last session (persisted client-side by
  // projectStore), resume straight into it instead of the bare Workbench —
  // returning users shouldn't have to reopen their app every login. Falls
  // back to `fallback` (the backend's /workbench redirect) when there's no
  // remembered project, or the remembered one no longer resolves (deleted,
  // or belongs to a different account that used this browser).
  const resolveLandingPath = useCallback(async (fallback: string) => {
    const store = useProjectStore.getState()
    if (!store.currentProject) {
      await store.restoreProject()
    }
    const resumed = useProjectStore.getState().currentProject
    return resumed
      ? `/workbench/${encodeURIComponent(resumed.application.folder_name)}/next`
      : fallback
  }, [])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showSessionModal, setShowSessionModal] = useState(false)
  const [activeFeature, setActiveFeature] = useState(0)

  // Redirect if already authenticated. Resumes into the last-open project
  // when there is one, otherwise lands on the Workbench; an explicit
  // ?next= deep-link still wins inside goNext().
  useEffect(() => {
    if (isAuthenticated) {
      resolveLandingPath('/workbench').then(goNext)
    }
  }, [isAuthenticated, goNext, resolveLandingPath])

  // Feature carousel auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURES.length)
    }, 4000)
    return () => clearInterval(timer)
  }, [])

  // A password sign-in parked on its second factor: the code step, or (when
  // the organization requires MFA and the grace period is over) setup.
  const [mfa, setMfa] = useState<{ kind: 'verify' | 'enrol'; token: string } | null>(null)
  // The setup answer (session already open) is acted on only after the user
  // has saved their recovery codes.
  const enrolled = useRef<Record<string, any> | null>(null)

  // Every sign-in answer — password, takeover, code, setup — ends here.
  const handleResult = useCallback(async (result: Record<string, any>) => {
    if (result.needs_activation) {
      toast.error('Please verify your email to continue. Enter the code we sent you.')
      navigate(`/activate?email=${encodeURIComponent(email)}`, { replace: true })
      return
    }
    if (result.existing_session) {
      setShowSessionModal(true)
      return
    }
    if (result.mfa_required) {
      setShowSessionModal(false)
      setMfa({ kind: 'verify', token: result.mfa_token })
      return
    }
    if (result.mfa_enrolment_required) {
      setShowSessionModal(false)
      setMfa({ kind: 'enrol', token: result.mfa_token })
      return
    }
    setShowSessionModal(false)
    if (typeof result.recovery_codes_remaining === 'number') {
      toast(`You have ${result.recovery_codes_remaining} recovery code${result.recovery_codes_remaining === 1 ? '' : 's'} left — make new ones in Settings → Account.`)
    }
    if (result.mfa_enrol_by) {
      toast(`Your organization requires two-factor sign-in. Set it up in Settings → Account before ${new Date(result.mfa_enrol_by).toLocaleDateString()}.`, { duration: 8000 })
    }
    if (result.password_change_required && result.redirect) {
      await refreshUser()
      toast('Your organization requires a new password — please choose one now.')
      // Not goNext: an expired password comes before any ?next destination.
      navigate(result.redirect, { replace: true })
    } else if (result.redirect) {
      await refreshUser()
      goNext(await resolveLandingPath(result.redirect))
    }
  }, [email, navigate, refreshUser, goNext, resolveLandingPath])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Please enter email and password')
      return
    }
    setLoading(true)
    // Explicit sign-in: allow auto-SSO again after a prior logout suppressed it.
    clearSsoSuppressed()
    try {
      await handleResult(await login({ email, password }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Login failed'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleSessionChoice = useCallback(async (choice: 'terminate' | 'cancel') => {
    if (choice === 'cancel') {
      setShowSessionModal(false)
      return
    }
    setLoading(true)
    try {
      await handleResult(await confirmLoginChoice({ email, password, user_choice: choice }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to process'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [email, password, handleResult])

  const backToPassword = useCallback(() => { setMfa(null); setPassword('') }, [])

  return (
    <div className="login-layout" style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'var(--color-bg)',
    }}>
      {/* Left Column - Marketing */}
      <div className="login-left" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '3rem 4rem',
        background: 'var(--color-bg-secondary)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative gradient orb */}
        <div style={{
          position: 'absolute',
          top: -120,
          left: -120,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,97,208,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -100,
          right: -100,
          width: 350,
          height: 350,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,153,116,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Brand */}
          <div style={{ marginBottom: '1.5rem' }}>
            <img
              src={logoLarge}
              alt="Twasta.ai"
              style={{ height: 48, objectFit: 'contain' }}
            />
          </div>

          {/* Tagline */}
          <h1 style={{
            fontSize: '2.0625rem',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: '0.75rem',
            color: 'var(--color-text)',
          }}>
            Describe. Review. Deploy.
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'var(--color-text-secondary)',
            marginBottom: '2.5rem',
            maxWidth: 480,
            lineHeight: 1.7,
          }}>
            Build complete business applications from natural language descriptions.
            AI-powered code generation meets visual design tools.
          </p>

          {/* Feature cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {FEATURES.map((feature, index) => (
              <div
                key={index}
                onClick={() => setActiveFeature(index)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  background: activeFeature === index
                    ? 'rgba(76, 141, 255, 0.06)'
                    : 'transparent',
                  borderLeft: activeFeature === index
                    ? '3px solid var(--color-accent)'
                    : '3px solid transparent',
                  transition: 'all var(--transition-normal)',
                }}
              >
                <span className="material-icons" style={{
                  fontSize: 25,
                  color: activeFeature === index ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  marginTop: 2,
                  flexShrink: 0,
                }}>
                  {feature.icon}
                </span>
                <div>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: activeFeature === index ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    marginBottom: 4,
                  }}>
                    {feature.title}
                  </div>
                  {activeFeature === index && (
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.6,
                      animation: 'fadeIn 0.3s ease-out',
                    }}>
                      {feature.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Feature indicator dots */}
          <div style={{
            display: 'flex',
            gap: 8,
            marginTop: '1.5rem',
            paddingLeft: '1.25rem',
          }}>
            {FEATURES.map((_, idx) => (
              <div
                key={idx}
                onClick={() => setActiveFeature(idx)}
                style={{
                  width: idx === activeFeature ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: idx === activeFeature
                    ? 'var(--color-accent)'
                    : 'var(--color-border)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-normal)',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Column - Login Form */}
      <div className="login-right" style={{
        width: 480,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '3rem',
      }}>
        <div style={{ maxWidth: 380, width: '100%', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '1.5625rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
          }}>
            Welcome back
          </h2>
          <p style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text-secondary)',
            marginBottom: '2rem',
          }}>
            {/* Someone arriving from a `twasta new` sign-in link did not come
                here for "their workspace" — they came to approve a computer,
                and a generic login screen looks like the wrong page. Say why
                they are being asked. */}
            {nextPath && nextPath.startsWith('/agent-access')
              ? 'Log in to approve access for your computer — the account you '
                + 'sign in as is the one the agent will act as'
              : 'Sign in to continue to your workspace'}
          </p>

          {mfa?.kind === 'verify' && (
            <MfaCodeForm
              onSubmit={async (code) => { await handleResult(await mfaVerify(mfa.token, code)) }}
              onCancel={backToPassword}
            />
          )}
          {mfa?.kind === 'enrol' && (
            <MfaSetup
              intro="Your organization requires two-factor sign-in. Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows."
              start={() => mfaEnrolStart(mfa.token)}
              confirm={async (code) => {
                const res = await mfaEnrolConfirm(mfa.token, code)
                enrolled.current = res
                return res
              }}
              onDone={() => { if (enrolled.current) void handleResult(enrolled.current) }}
              onCancel={backToPassword}
            />
          )}

          {!mfa && (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Link to="/forgot-password" style={{ fontSize: '0.875rem' }}>
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          )}

          {signupEnabled ? (
            <>
              <div style={{
                textAlign: 'center',
                marginTop: '1.5rem',
                fontSize: '0.9375rem',
                color: 'var(--color-text-secondary)',
              }}>
                Don't have an account?{' '}
                <Link to="/register">Create one</Link>
              </div>

              <div style={{
                textAlign: 'center',
                marginTop: '0.75rem',
                fontSize: '0.875rem',
                color: 'var(--color-text-secondary)',
              }}>
                <Link to="/waitlist" style={{ color: 'var(--color-text-secondary)' }}>
                  Join the waitlist
                </Link>
              </div>
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              marginTop: '1.5rem',
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
            }}>
              Twasta.ai is currently in beta — access is invite-only.
              <div style={{ marginTop: '0.375rem' }}>
                <Link to="/waitlist">Join the waitlist</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Existing Session Modal */}
      {showSessionModal && (
        <div className="overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">Active Session Detected</h3>
              <button className="modal-close" onClick={() => setShowSessionModal(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <p style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
              marginBottom: '1.25rem',
              lineHeight: 1.7,
            }}>
              You are already logged in from another device or browser.
              Would you like to terminate the existing session and continue here?
            </p>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => handleSessionChoice('cancel')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSessionChoice('terminate')}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                ) : (
                  <>
                    <span className="material-icons" style={{ fontSize: 19 }}>logout</span>
                    Terminate & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
